import { dsmApi } from "./dsm-api";
import { DEMO_SCOPE_CATALOG } from "./demo-data";
import {
  classificationKey,
  foldToPair,
  topOf,
  type Classification
} from "./classification";
import {
  createTemplateRecord,
  loadTemplate,
  loadTemplates,
  softDeleteTemplateRecord,
  templateOriginsPresent
} from "./dsm-templates-remote";
import type {
  DsmTemplate,
  Lod,
  ScopeStatus,
  TemplateItem,
  TemplateSource
} from "./types";

// Seed defaults when a template item carries none — ScopeItemInput requires
// status/responsibility/notes, and "not started, architect-owned" is the
// neutral starting point for freshly seeded scope.
const DEFAULT_STATUS: ScopeStatus = "NOT_STARTED";
const DEFAULT_RESPONSIBILITY = "Architect";

const VALID_SYSTEMS = new Set(["UF", "MF", "CUSTOM"]);
const VALID_LODS = new Set([1, 2, 3, 4, 5]);
const VALID_STATUSES = new Set([
  "NOT_STARTED",
  "IN_PROGRESS",
  "IN_REVIEW",
  "COMPLETE"
]);

function classificationList(
  stored: Record<string, unknown> | undefined
): unknown[] | null {
  if (!stored) return null;
  if ("system" in stored) return [stored];
  return [stored.uf, stored.mf, stored.label].filter(Boolean);
}

function templateClassificationSource(
  entry: Record<string, unknown>
): unknown[] | null {
  const stored = entry.classification as Record<string, unknown> | undefined;
  const fromClassification = classificationList(stored);
  if (fromClassification) return fromClassification;
  if (Array.isArray(entry.classifications)) return entry.classifications;
  if (Array.isArray(entry.hierarchy)) return entry.hierarchy;
  return null;
}

/**
 * Validate an uploaded template JSON payload. Returns sanitized items
 * (unknown fields dropped) or throws a descriptive Error pointing at the
 * first offending entry.
 */
export function parseTemplateFile(json: unknown): TemplateItem[] {
  if (!Array.isArray(json)) {
    throw new Error("Template file must be a JSON array of scope items.");
  }
  if (!json.length) {
    throw new Error("Template file contains no items.");
  }
  return json.map((raw, index) => {
    const at = `Item ${index + 1}`;
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      throw new Error(`${at}: expected an object.`);
    }
    const entry = raw as Record<string, unknown>;
    const { description } = entry;
    // Accept every shape a sample file may already be in: a v3 single
    // `classification`, a v2 `classifications` set, or a v1 `hierarchy`
    // ladder. Folding them here is the same collapse the read migration does.
    // A v3 file holds one entry under `classification`; a v4 file holds the
    // slot container. Flattening the container here lets both round-trip.
    const source = templateClassificationSource(entry);
    if (!source || !source.length) {
      throw new Error(`${at}: "classification" is required.`);
    }
    const parsed: Classification[] = source.map((lvl, li) => {
      const lvlAt = `${at}, entry ${li + 1}`;
      if (typeof lvl !== "object" || lvl === null) {
        throw new Error(`${lvlAt}: expected an object.`);
      }
      const level = lvl as Record<string, unknown>;
      if (
        typeof level.system !== "string" ||
        !VALID_SYSTEMS.has(level.system)
      ) {
        throw new Error(`${lvlAt}: "system" must be "UF", "MF" or "CUSTOM".`);
      }
      if (typeof level.code !== "string" || typeof level.title !== "string") {
        throw new Error(`${lvlAt}: "code" and "title" must be strings.`);
      }
      if (level.system === "CUSTOM" && level.code.trim()) {
        throw new Error(`${lvlAt}: a custom entry must have no code.`);
      }
      if (level.system !== "CUSTOM" && !level.code.trim()) {
        throw new Error(`${lvlAt}: "code" is required for UF and MF entries.`);
      }
      return {
        system: level.system as Classification["system"],
        code: level.code,
        title: level.title,
        ...(typeof level.termId === "string" ? { termId: level.termId } : {})
      };
    });
    const classification = foldToPair(parsed);
    if (typeof description !== "string") {
      throw new Error(`${at}: "description" must be a string.`);
    }
    if (
      entry.defaultLod !== undefined &&
      !VALID_LODS.has(entry.defaultLod as number)
    ) {
      throw new Error(`${at}: "defaultLod" must be 1-5.`);
    }
    if (
      entry.responsibility !== undefined &&
      typeof entry.responsibility !== "string"
    ) {
      throw new Error(`${at}: "responsibility" must be a string.`);
    }
    if (
      entry.status !== undefined &&
      !VALID_STATUSES.has(entry.status as string)
    ) {
      throw new Error(`${at}: "status" is not a valid scope status.`);
    }
    return {
      ...(classification ? { classification } : {}),
      description,
      ...(entry.defaultLod !== undefined
        ? { defaultLod: entry.defaultLod as Lod }
        : {}),
      ...(entry.responsibility !== undefined
        ? { responsibility: entry.responsibility as string }
        : {}),
      ...(entry.status !== undefined
        ? { status: entry.status as ScopeStatus }
        : {})
    };
  });
}

// One in-flight mock-seed guard so a StrictMode double-mount (or two eager
// callers) can't POST the two samples twice.
let mockSeedInFlight: Promise<void> | null = null;

export const dsmTemplatesApi = {
  async listTemplates(): Promise<DsmTemplate[]> {
    const rows = await loadTemplates();
    return rows.sort(
      (a, b) =>
        a.name.localeCompare(b.name) || a.createdAt.localeCompare(b.createdAt)
    );
  },

  async getTemplate(id: string): Promise<DsmTemplate | undefined> {
    return loadTemplate(id);
  },

  async createTemplate(
    name: string,
    items: TemplateItem[],
    source: TemplateSource
  ): Promise<DsmTemplate> {
    return createTemplateRecord(name, items, source);
  },

  /**
   * Snapshot a project's scope as a reusable template. Only classification
   * + defaults survive — commitments/bids reference that project's packages
   * and would dangle anywhere else.
   */
  async createTemplateFromProject(
    projectId: string,
    name: string
  ): Promise<DsmTemplate> {
    const scopeItems = await dsmApi.listScopeItems(projectId);
    const items: TemplateItem[] = scopeItems.map((it) => ({
      ...(it.classification ? { classification: it.classification } : {}),
      description: it.description,
      ...(it.commitments.length ? { defaultLod: it.commitments[0].lod } : {}),
      responsibility: it.responsibility,
      status: it.status
    }));
    return this.createTemplate(name, items, "project");
  },

  async deleteTemplate(id: string): Promise<void> {
    await softDeleteTemplateRecord(id);
  },

  /**
   * Append a template's items into a project, skipping any whose full
   * classification path already exists (re-applying a template must never
   * duplicate scope). Returns how many were added vs skipped.
   */
  async seedProjectFromTemplate(
    projectId: string,
    templateId: string
  ): Promise<{ added: number; skipped: number }> {
    const template = await loadTemplate(templateId);
    if (!template) throw new Error("Template not found.");
    const existing = await dsmApi.listScopeItems(projectId);
    const seen = new Set(
      existing.map((it) => classificationKey(it.classification, it.description))
    );
    let added = 0;
    let skipped = 0;
    for (const item of template.items) {
      const key = classificationKey(item.classification, item.description);
      if (seen.has(key)) {
        skipped += 1;
        continue;
      }
      seen.add(key);
      // Sequential on purpose: the running `seen` set must observe earlier
      // items to dedupe within the template itself.

      await dsmApi.createScopeItem(projectId, {
        ...(item.classification ? { classification: item.classification } : {}),
        description: item.description,
        status: item.status ?? DEFAULT_STATUS,
        responsibility: item.responsibility ?? DEFAULT_RESPONSIBILITY,
        notes: "",
        commitments: [],
        bids: []
      });
      added += 1;
    }
    return { added, skipped };
  },

  /**
   * Idempotently seed the two demo templates at the subscription level. Skipped
   * once any mock template has ever existed (even a deleted one), so the user
   * can remove a sample without it coming back on the next visit.
   */
  async ensureMockTemplates(): Promise<void> {
    if (mockSeedInFlight) return mockSeedInFlight;
    mockSeedInFlight = (async () => {
      const origins = await templateOriginsPresent();
      if (origins.has("mock")) return;
      const coreAndShell: TemplateItem[] = DEMO_SCOPE_CATALOG.slice(0, 20).map(
        (entry, i) => ({
          ...(entry.classification
            ? { classification: entry.classification }
            : {}),
          description: entry.description,
          defaultLod: ((i % 5) + 1) as Lod
        })
      );
      const mepFitOut: TemplateItem[] = DEMO_SCOPE_CATALOG.filter((entry) => {
        return topOf(entry.classification, "UF") === "D";
      }).map((entry) => ({
        ...(entry.classification
          ? { classification: entry.classification }
          : {}),
        description: entry.description
      }));
      await createTemplateRecord(
        "Stadium — Core & Shell",
        coreAndShell,
        "mock"
      );
      await createTemplateRecord("Stadium — MEP Fit-out", mepFitOut, "mock");
    })().finally(() => {
      mockSeedInFlight = null;
    });
    return mockSeedInFlight;
  }
};
